import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useGallery } from "../context/GalleryContext";
import SectionLabel from "../components/SectionLabel";
import { artImage } from "../utils/image";
import { tokens } from "../theme";
import { INQUIRY_EMAIL, INSTAGRAM_URL } from "../auth";

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.66rem",
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: tokens.brass,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: tokens.display,
          fontSize: "1.25rem",
          color: tokens.ink,
          mt: 0.5,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function About() {
  const { artworks } = useGallery();
  const portrait =
    artworks.find((a) => a.id === "river-sunset") ??
    artworks.find((a) => a.featured) ??
    artworks[0];

  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 9 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
          gap: { xs: 5, md: 9 },
          alignItems: "start",
        }}
      >
        <Box sx={{ position: { md: "sticky" }, top: { md: 96 } }}>
          {portrait && (
            <Box
              sx={{
                bgcolor: tokens.paper,
                p: 1.4,
                border: `1px solid ${tokens.line}`,
                boxShadow: "0 20px 50px rgba(27,26,23,0.12)",
              }}
            >
              <Box
                component="img"
                src={artImage(portrait)}
                alt="A painting by TL"
                sx={{ width: "100%", display: "block" }}
              />
            </Box>
          )}
        </Box>

        <Box>
          <SectionLabel>About</SectionLabel>
          <Typography
            sx={{
              mt: 2.5,
              fontSize: "0.74rem",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: tokens.brass,
            }}
          >
            Tracy Lavely
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontFamily: tokens.display,
              fontSize: { xs: "1.15rem", md: "1.3rem" },
              color: tokens.ink,
            }}
          >
            Acrylic Palette Knife
          </Typography>
          <Typography
            variant="h1"
            sx={{ mt: 2.5, fontSize: { xs: "2.6rem", md: "3.8rem" } }}
          >
            Made by hand,
            <br />
            <Box
              component="span"
              sx={{ fontStyle: "italic", color: tokens.accent }}
            >
              one at a time.
            </Box>
          </Typography>

          <Box
            sx={{
              mt: 4,
              maxWidth: 560,
              "& p": {
                fontSize: "1.1rem",
                lineHeight: 1.78,
                color: tokens.taupe,
                mb: 2.5,
              },
            }}
          >
            <Typography component="p">
              Tracy hails from Indianapolis, Indiana, and credits her hometown
              as the source of her early love of art, where she also met her
              husband of 43 years. Her mother was an artist, and young Tracy
              spent many hours absorbing her mother&apos;s lessons. Her sister
              is also an artist, and they continue to support each other&apos;s
              work.
            </Typography>
            <Typography component="p">
              After college, Tracy majored in Criminal Justice and eventually
              began her career with the Virginia Department of Corrections. She
              served as Chief Probation and Parole Officer for Fairfax County,
              Virginia, leading a large office and staff, implementing creative
              programming, and collaborating with community stakeholders to help
              people grow and keep communities safe.
            </Typography>
            <Typography component="p">
              Tracy kept painting throughout those years and, about 15 years
              ago, recommitted to creating regularly. After retiring in 2021,
              she began experimenting with a palette knife. As a self-taught
              artist, she found that laying color with a palette knife allows
              her to create the texture and movement seen across her beach and
              coastal-themed paintings.
            </Typography>
            <Typography component="p">
              Her work is often showcased at{" "}
              <Box
                component="a"
                href="https://www.pastagalleryart.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: tokens.ink,
                  textDecorationColor: tokens.line,
                  textUnderlineOffset: "0.18em",
                  "&:hover": { color: tokens.accent },
                }}
              >
                PAStA (Professional Artists of St. Augustine) Fine Art Gallery
              </Box>
              , where she looks forward to meeting collectors and visitors.
            </Typography>
            <Typography component="p">
              Follow on Instagram:{" "}
              <Box
                component="a"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: tokens.ink,
                  textDecorationColor: tokens.line,
                  textUnderlineOffset: "0.18em",
                  "&:hover": { color: tokens.accent },
                }}
              >
                @tlart2023
              </Box>
            </Typography>
            <Typography
              component="p"
              sx={{ fontStyle: "italic", color: tokens.ink }}
            >
              "Change is inevitable, growth is optional"
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: { xs: 4, md: 7 },
              flexWrap: "wrap",
              py: 3,
              borderTop: `1px solid ${tokens.line}`,
              borderBottom: `1px solid ${tokens.line}`,
            }}
          >
            <Fact label="Studio" value="St. Augustine, Florida" />
            <Fact label="Media" value="Acrylic · Palette Knife" />
            <Fact label="Each piece" value="One of a kind" />
          </Box>

          <Button
            component="a"
            href={`mailto:${INQUIRY_EMAIL}`}
            variant="contained"
            color="primary"
            sx={{ mt: 4, py: 1.3 }}
          >
            Inquire or commission
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
